'use client'

// React Imports
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'

// MUI Imports
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'

// Component Imports
import KPICards from './KPICards'
import RevenueVsExpensesChart from './RevenueVsExpensesChart'
import QuickInsightsWidget from './QuickInsightsWidget'
import CategoryChart from './CategoryChart'
import UpcomingPayments from './UpcomingPayments'
import MonthlyFinancialChart from './MonthlyFinancialChart'
import BankAccountsChart from './BankAccountsChart'
import WeatherWidget from './WeatherWidget'
import ChartErrorBoundary from '@/components/shared/ChartErrorBoundary'

// Hook Imports
import { useFarmChangeHandler } from '@/hooks/useFarmChangeHandler'
import { useDashboardResumeTranslations, useTranslations } from '@/hooks/useTranslations'
import useAuth from '@/hooks/useAuth'
import { useFarm } from '@/hooks/api/useFarm'

// Utils Imports
import axiosInstance from '@/utils/AxiosInstance'

// Types
import type { DashboardResumeData, DashboardResumeAPIResponse, KPIItemData, KPICompositeData } from '@/types/dashboards/resumeTypes'

const DashboardResume = () => {
  const { t: tDashboard } = useDashboardResumeTranslations()
  const { t: tCommon } = useTranslations()
  const { user } = useAuth()
  const { selectedFarmID } = useFarm()

  // Estados
  const [dashboardData, setDashboardData] = useState<DashboardResumeData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Referência para controlar cache e evitar nova chamada ao trocar idioma
  const dataCache = useRef<DashboardResumeData | null>(null)
  const hasInitialLoad = useRef(false)
  const isFirstLoad = useRef(true)

  // Função para criar objeto padrão quando há erro
  const createDefaultData = useCallback((): DashboardResumeData => {
    const defaultKPIItem: KPIItemData = { value: 0, previousValue: 0, variation: 'neutral', percent: '0' }
    const defaultComposite: KPICompositeData = { actual: { ...defaultKPIItem }, forecast: { ...defaultKPIItem } }

    return {
      kpiData: {
        monthEntries: defaultComposite,
        monthExits: defaultComposite,
        netProfit: defaultKPIItem,
        estimatedProfit: defaultKPIItem,
        availableBalance: defaultKPIItem,
        cashSurplus: defaultComposite,
        profitMargin: defaultComposite,
        pendingExpenses: defaultKPIItem
      },
      monthlyRevenueExpenses: [],
      monthlyFinancialFlow: [],
      expensesByCategory: [],
      revenuesByCategory: [],
      quickInsights: {
        keyIndicators: [],
        goalsProgress: [],
        alerts: []
      },
      upcomingPayments: [],
      bankAccounts: []
    }
  }, [])

  // Hook para detectar mudança de fazenda e recarregar dados
  useFarmChangeHandler({
    invalidateCache: true,
    reloadDataOnChange: false,
    onFarmChange: () => {
      // Resetar estados para forçar novo carregamento
      setDashboardData(createDefaultData())

      // Limpar cache e forçar recarregamento
      dataCache.current = null
      hasInitialLoad.current = false
      isFirstLoad.current = true

      setTimeout(() => {
        fetchDashboardData()
      }, 200)
    }
  })

  // Função para determinar período do dia
  const getTimeOfDay = useCallback((): string => {
    const hour = new Date().getHours()

    if (hour >= 5 && hour < 12) {
      return tCommon('greetings.goodMorning')
    } else if (hour >= 12 && hour < 18) {
      return tCommon('greetings.goodAfternoon')
    } else {
      return tCommon('greetings.goodEvening')
    }
  }, [tCommon])

  // Função para obter primeiro nome do usuário (mesma lógica do UserDropdown)
  const getFirstName = useCallback((): string => {
    const fullName = user?.name || user?.fullName

    if (!fullName) return ''

    return fullName.split(' ')[0]
  }, [user?.name, user?.fullName])

  // Mensagem de saudação
  const greetingMessage = useMemo(() => {
    const firstName = getFirstName()
    const timeGreeting = getTimeOfDay()

    if (firstName) {
      return `${timeGreeting}, ${firstName}!`
    }

    return timeGreeting
  }, [getFirstName, getTimeOfDay])

  // Função para buscar dados do dashboard
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await axiosInstance.get<DashboardResumeAPIResponse>(`${process.env.NEXT_PUBLIC_DASHBOARDS_PATH || ''}/v1/dashboard/resume`)

      if (response.data && response.data.success && response.data.data) {
        const data = response.data.data

        setDashboardData(data)
        dataCache.current = data
        hasInitialLoad.current = true
      } else {
        throw new Error('Formato de resposta inválido')
      }
    } catch (err: any) {
      console.error('Erro ao buscar dados do dashboard:', err)
      const errorMessage = err.response?.data?.message || 'Não foi possível carregar as informações do dashboard'

      setError(errorMessage)

      // Definir dados padrão em caso de erro
      const defaultData = createDefaultData()

      setDashboardData(defaultData)
      dataCache.current = defaultData
    } finally {
      setLoading(false)
    }
  }, []) // Array vazio para evitar recriações e múltiplas requisições

  // Efeito para carregar dados na inicialização (apenas uma vez)
  useEffect(() => {
    // Previne execuções múltiplas durante desenvolvimento (Strict Mode)
    if (!isFirstLoad.current) {
      return
    }

    isFirstLoad.current = false

    // Se já temos dados em cache, não faz nova chamada
    if (hasInitialLoad.current && dataCache.current) {
      setDashboardData(dataCache.current)
      setLoading(false)

      return
    }

    fetchDashboardData()
  }, [fetchDashboardData])

  // Se houver erro, exibir alerta mas ainda renderizar os componentes com dados zerados
  const renderErrorAlert = () => {
    if (!error) return null

    return (
      <Alert severity='error' sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={4}>
        {/* Mensagem de Saudação e Widget de Clima */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: { xs: 'center', md: 'space-between' },
            width: '100%'
          }}
        >
          {/* Mensagem de Saudação */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: 250
            }}
          >
            <Typography variant='h4' component='h1' sx={{ mb: 1, fontWeight: 600 }}>
              {greetingMessage}
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              {tDashboard('subtitle')}
            </Typography>
          </Box>

          {/* Widget de Clima - Alinhado à direita */}
          <WeatherWidget />
        </Stack>

        {/* Alerta de Erro (se houver) */}
        {renderErrorAlert()}

        {/* KPIs Principais - Compactos */}
        <KPICards data={dashboardData?.kpiData} loading={loading} />

        {/* Seção Principal com Gráficos e Insights */}
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ alignItems: 'stretch' }}>
          {/* Coluna Esquerda - Gráficos */}
          <Box sx={{ flex: { xs: 1, lg: 2 } }}>
            <Stack spacing={3}>
              {/* Gráfico de Receitas vs Despesas */}
              <ChartErrorBoundary fallbackLabel='Receitas vs Despesas indisponível'>
                <RevenueVsExpensesChart data={dashboardData?.monthlyRevenueExpenses} loading={loading} />
              </ChartErrorBoundary>

              {/* Gráficos de Categoria - lado a lado */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'stretch' }}>
                <Box sx={{ flex: 1 }}>
                  <CategoryChart
                    title={tDashboard('charts.expensesByCategory')}
                    type='expenses'
                    data={dashboardData?.expensesByCategory}
                    loading={loading}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <CategoryChart
                    title={tDashboard('charts.revenuesByCategory')}
                    type='revenues'
                    data={dashboardData?.revenuesByCategory}
                    loading={loading}
                  />
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* Coluna Direita - Widget de Insights */}
          <Box sx={{ flex: { xs: 1, lg: 1 }, minWidth: { lg: 350 } }}>
            <QuickInsightsWidget data={dashboardData?.quickInsights} loading={loading} />
          </Box>
        </Stack>

        {/* Segunda Linha - Gráfico Financeiro Mensal e Saldos Bancários */}
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ alignItems: 'stretch' }}>
          {/* Gráfico de Fluxo de Caixa Mensal - 2/3 da tela */}
          <Box sx={{ flex: { xs: 1, lg: 2 } }}>
            <ChartErrorBoundary fallbackLabel='Fluxo financeiro mensal indisponível'>
              <MonthlyFinancialChart data={dashboardData?.monthlyFinancialFlow} loading={loading} />
            </ChartErrorBoundary>
          </Box>

          {/* Saldos Bancários - 1/3 da tela */}
          <Box sx={{ flex: { xs: 1, lg: 1 }, minWidth: { lg: 300 } }}>
            <BankAccountsChart accountsData={dashboardData?.bankAccounts} loading={loading} />
          </Box>
        </Stack>

        {/* Terceira Linha - Próximos Vencimentos (largura total) */}
        <Box sx={{ width: '100%' }}>
          <UpcomingPayments data={dashboardData?.upcomingPayments} loading={loading} />
        </Box>
      </Stack>
    </Box>
  )
}

export default DashboardResume
